import { useEffect, useState } from 'react';
import { DonViChiNhanh, DonViChiNhanhInput, DonViChiNhanhResponse } from '@/types/schemas/don-vi-chi-nhanh.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseDonViChiNhanhReturn {
  donViChiNhanhs: DonViChiNhanh[];
  isLoading: boolean;
  addDonViChiNhanh: (newDonViChiNhanh: DonViChiNhanhInput) => Promise<DonViChiNhanh>;
  updateDonViChiNhanh: (uuid: string, updatedDonViChiNhanh: DonViChiNhanhInput) => Promise<DonViChiNhanh>;
  deleteDonViChiNhanh: (uuid: string) => Promise<void>;
  refreshDonViChiNhanhs: () => Promise<void>;
  getDonViChiNhanhByCode: (code: string) => Promise<DonViChiNhanh | null>;
  getActiveDonViChiNhanhs: () => Promise<DonViChiNhanh[]>;
}

/**
 * Hook for managing DonViChiNhanh (Branch/Unit) data
 *
 * This hook provides functions to fetch, create, update, and delete branches/entity-units.
 */
export const useDonViChiNhanh = (initialDonViChiNhanhs: DonViChiNhanh[] = []): UseDonViChiNhanhReturn => {
  const [donViChiNhanhs, setDonViChiNhanhs] = useState<DonViChiNhanh[]>(initialDonViChiNhanhs);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchDonViChiNhanhs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<DonViChiNhanhResponse>(`/entities/${entity.slug}/erp/entity-units/`);
      setDonViChiNhanhs(response.data.results);
    } catch (error) {
      console.error('Error fetching branches/entity-units:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDonViChiNhanhByCode = async (code: string): Promise<DonViChiNhanh | null> => {
    if (!entity?.slug) return null;

    setIsLoading(true);
    try {
      const response = await api.get<DonViChiNhanhResponse>(
        `/entities/${entity.slug}/erp/entity-units/?ma_unit=${code}`
      );

      if (response.data.results.length > 0) {
        return response.data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching branch/unit by code:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getActiveDonViChiNhanhs = async (): Promise<DonViChiNhanh[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonViChiNhanhResponse>(`/entities/${entity.slug}/erp/entity-units/?active=true`);
      return response.data.results;
    } catch (error) {
      console.error('Error fetching active branches/entity-units:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addDonViChiNhanh = async (newDonViChiNhanh: DonViChiNhanhInput): Promise<DonViChiNhanh> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.post<DonViChiNhanh>(`/entities/${entity.slug}/erp/entity-units/`, newDonViChiNhanh);
      setDonViChiNhanhs(prev => [...prev, response.data]);
      return response.data;
    } catch (error) {
      console.error('Error adding branch/unit:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateDonViChiNhanh = async (
    uuid: string,
    updatedDonViChiNhanh: DonViChiNhanhInput
  ): Promise<DonViChiNhanh> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.patch<DonViChiNhanh>(
        `/entities/${entity.slug}/erp/entity-units/${uuid}/`,
        updatedDonViChiNhanh
      );
      setDonViChiNhanhs(prev => prev.map(item => (item.uuid === uuid ? response.data : item)));
      return response.data;
    } catch (error) {
      console.error('Error updating branch/unit:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDonViChiNhanh = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/entity-units/${uuid}/`);
      setDonViChiNhanhs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting branch/unit:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDonViChiNhanhs();
  }, [entity?.slug]);

  return {
    donViChiNhanhs,
    isLoading,
    addDonViChiNhanh,
    updateDonViChiNhanh,
    deleteDonViChiNhanh,
    refreshDonViChiNhanhs: fetchDonViChiNhanhs,
    getDonViChiNhanhByCode,
    getActiveDonViChiNhanhs
  };
};
